import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Drawer,
  Box,
  Avatar,
  Divider,
  alpha,
  Paper
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  Quiz as QuizIcon,
  LiveTv as LiveTvIcon,
  Groups as GroupsIcon,
  Payments as PaymentsIcon,
  DateRange as DateRangeIcon,
  Domain as DomainIcon,
  Message as MessageIcon,
  Logout as LogoutIcon,
  EmojiEvents as AchievementsIcon
} from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { APP_CHROME_OFFSET } from '../../../theme/chromeOffset';

interface MenuItem {
  header?: string;
  text?: string;
  icon?: React.ReactNode;
  path?: string;
  roles?: string[];
}

const menuItems: MenuItem[] = [
  { header: 'Overview' },
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
  { text: 'Reports', icon: <BarChartIcon />, path: '/admin/reports' },

  { header: 'Management' },
  { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
  { text: 'Tenants', icon: <DomainIcon />, path: '/admin/tenants', roles: ['super_admin'] },
  { text: 'Teams', icon: <GroupsIcon />, path: '/admin/teams' },

  { header: 'Learning' },
  { text: 'Sessions', icon: <DateRangeIcon />, path: '/admin/sessions' },
  { text: 'Enrollments', icon: <SchoolIcon />, path: '/admin/enrollments' },
  { text: 'Quizzes', icon: <QuizIcon />, path: '/admin/quizzes' },
  { text: 'Live Quizzes', icon: <LiveTvIcon />, path: '/admin/live' },
  { text: 'Problems', icon: <AssignmentIcon />, path: '/admin/problems' },
  { text: 'Achievements', icon: <AchievementsIcon />, path: '/admin/achievements' },

  { header: 'System' },
  { text: 'Payments', icon: <PaymentsIcon />, path: '/admin/payments' },
  { text: 'Messages', icon: <MessageIcon />, path: '/admin/messages' },
];

interface AdminSidebarProps {
  open: boolean;
  toggle: () => void;
  drawerWidth: number;
  collapsedWidth: number;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, drawerWidth, collapsedWidth }) => {
  const theme = useTheme();
  const location = useLocation();
  const { logout, authState } = useAuth();
  const user = authState.user;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header / Logo Area */}
      <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'flex-start' : 'center',
        minHeight: 80
      }}>
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.main,
            width: 40,
            height: 40,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
          }}
        >
          MW
        </Avatar>
        {open && (
          <Box sx={{ ml: 2, overflow: 'hidden' }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              MathWiz
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Admin Console
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2, mx: 3, opacity: 0.5 }} />

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 2, overflowY: 'auto' }}>
        {menuItems.map((item, index) => {
          // Check role-based access
          if (item.roles && !item.roles.includes(user?.role || '')) {
            return null;
          }

          if (item.header) {
            return open ? (
              <Typography
                key={`header-${index}`}
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ px: 2, mt: 3, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
              >
                {item.header}
              </Typography>
            ) : <Divider key={`divider-${index}`} sx={{ my: 2, opacity: 0.5 }} />;
          }

          const path = item.path || '';
          const isActive = location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={NavLink}
                to={path}
                sx={{
                  borderRadius: 2,
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  color: isActive ? theme.palette.primary.main : 'text.secondary',
                  '&:hover': {
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.05),
                    color: isActive ? theme.palette.primary.main : 'text.primary',
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    color: 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: isActive ? 600 : 500, fontSize: '0.95rem' }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
                {isActive && open && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User Profile / Logout Area */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            p: open ? 2 : 1,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'space-between' : 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha(theme.palette.background.default, 0.8),
              borderColor: theme.palette.primary.main
            }
          }}
          onClick={handleLogout}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: user?.profile?.profileColor || theme.palette.primary.main,
                fontSize: '0.875rem'
              }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
            {open && (
              <Box sx={{ ml: 1.5, overflow: 'hidden' }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {user ? `${user.firstName} ${user.lastName}` : 'Admin User'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Sign Out
                </Typography>
              </Box>
            )}
          </Box>
          {open && <LogoutIcon fontSize="small" color="action" />}
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          bgcolor: 'background.paper',
          boxShadow: '4px 0 24px rgba(0,0,0,0.02)',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          // MUI's permanent Drawer resolves `top`/`height` against the viewport. Offsetting below
          // any global chrome (see theme/chromeOffset.ts) keeps this from rendering underneath it;
          // the var is unset outside the sales demo, so this is a no-op there.
          top: APP_CHROME_OFFSET,
          height: `calc(100% - ${APP_CHROME_OFFSET})`,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default AdminSidebar;
